/* eslint-disable @typescript-eslint/no-explicit-any */
'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import { CONSENT_COOKIE, hasConsent } from 'utils/attribution/consent';
import { isEUVisitor } from 'utils/geo/region';

const CONSENT_UPDATED_EVENT = 'tracking-consent-updated';
const SEGMENT_WRITE_KEY = '8KdF5Q1KaeJZrF7SDsBWFLlePWSFloAV';
const SEGMENT_DEBUG_KEY = 'segment_form_debug_logs';

// Non-EU visitors load on first interaction (EU already worked this way)
// or after this fallback, whichever comes first. Events fired before the
// SDK is ready are buffered by the snippet queue and analyticsClient, so
// nothing is dropped either way. The fallback is deliberately short (1s)
// as a business decision: marketing requires analytics to fire for fully
// passive sessions almost immediately, accepting that a timer this short
// lands back inside the PSI/Lighthouse trace window.
const NON_EU_FALLBACK_MS = 1000;

const shouldLoadScript = (): boolean => {
  if (isEUVisitor()) {
    return hasConsent('marketingAndAnalytics');
  }

  return true;
};

const isSegmentReady = (): boolean =>
  typeof window !== 'undefined' &&
  !!(window as any).analytics &&
  typeof (window as any).analytics.track === 'function' &&
  (window as any).analytics.initialized;

const LazySegmentScript = () => {
  const [snippetInjected, setSnippetInjected] = useState(false);
  const [userInteracted, setUserInteracted] = useState(false);
  const firedPageRef = useRef(false);

  const firePageIfAllowed = useCallback(() => {
    if (firedPageRef.current) {
      return;
    }
    if (!shouldLoadScript()) {
      return;
    }
    if (!isSegmentReady()) {
      return;
    }

    firedPageRef.current = true;

    try {
      (window as any).analytics.page();
    } catch {
      // swallow
    }
  }, []);

  const injectSegmentSnippet = useCallback(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (snippetInjected || document.getElementById('lazy-segment-script')) {
      return;
    }

    if ((window as any).analytics?.invoked || (window as any).analytics?.initialized) {
      setSnippetInjected(true);

      return;
    }

    const existingScript = document.querySelector('script[src*="cdn.segment.com/analytics.js"]');
    if (existingScript) {
      setSnippetInjected(true);

      return;
    }

    const script = document.createElement('script');
    script.id = 'lazy-segment-script';
    script.type = 'text/javascript';
    script.async = true;

    script.innerHTML = `
      !function(){
        var i="analytics",analytics=window[i]=window[i]||[];
        if(!analytics.initialize && !analytics.invoked){
          analytics.invoked=!0;
          analytics.methods=["trackSubmit","trackClick","trackLink","trackForm","pageview","identify","reset","group","track","ready","alias","debug","page","screen","once","off","on","addSourceMiddleware","addIntegrationMiddleware","setAnonymousId","addDestinationMiddleware","register"];
          
          analytics.factory=function(e){
            return function(){
              if(window[i].initialized) return window[i][e].apply(window[i],arguments);
              var n=Array.prototype.slice.call(arguments);
              if(["track","screen","alias","group","page","identify"].indexOf(e)>-1){
                var c=document.querySelector("link[rel='canonical']");
                n.push({__t:"bpc",c:c&&c.getAttribute("href")||void 0,p:location.pathname,u:location.href,s:location.search,t:document.title,r:document.referrer})
              }
              n.unshift(e);
              analytics.push(n);
              return analytics
            }
          };
          
          for(var n=0;n<analytics.methods.length;n++){
            var key=analytics.methods[n];
            analytics[key]=analytics.factory(key)
          }
          
          analytics.load=function(key,n){
            if(window._segmentLoading){
              return;
            }
            window._segmentLoading=true;
            
            var t=document.createElement("script");
            t.type="text/javascript";
            t.async=!0;
            t.setAttribute("data-global-segment-analytics-key",i);
            t.src="https://cdn.segment.com/analytics.js/v1/" + key + "/analytics.min.js";
            
            var r=document.getElementsByTagName("script")[0];
            r.parentNode.insertBefore(t,r);
            analytics._loadOptions=n
          };
          
          analytics._writeKey="${SEGMENT_WRITE_KEY}";
          analytics.SNIPPET_VERSION="5.2.0";

          // Only call analytics.load() when no consent cookie exists.
          // When the cookie exists, @segment/consent-manager handles loading
          // via conditionallyLoadAnalytics — calling load() here would set
          // analytics.initialized=true before the library runs, triggering
          // its internal window.location.reload() in an infinite loop.
          if(!window._segmentLoadAttempted && document.cookie.indexOf("tracking-preferences") === -1){
            window._segmentLoadAttempted = true;
            analytics.load("${SEGMENT_WRITE_KEY}");
            analytics.page();
          }
        }
      }();
    `;

    document.body.appendChild(script);
    setSnippetInjected(true);
  }, [snippetInjected]);

  // Returning visitors with a consent cookie are loaded by
  // @segment/consent-manager (conditionallyLoadAnalytics), which throws if
  // the analytics stub does not exist when it mounts. Inject the stub right
  // away for them; the snippet never calls load() when the cookie exists,
  // so the saved preferences stay in charge. First-time visitors (every
  // PSI lab run) keep the interaction gate below.
  useEffect(() => {
    if (typeof document !== 'undefined' && document.cookie.includes(CONSENT_COOKIE)) {
      injectSegmentSnippet();
    }
  }, [injectSegmentSnippet]);

  useEffect(() => {
    if (typeof window === 'undefined' || userInteracted) {
      return;
    }

    const handleInteraction = () => {
      setUserInteracted(true);
    };

    const events = [
      'mousedown',
      'mousemove',
      'keydown',
      'touchstart',
      'touchmove',
      'scroll',
      'wheel',
      'click',
      'pointerdown',
      'pointermove',
    ];

    events.forEach(event => {
      window.addEventListener(event, handleInteraction, { once: true, passive: true });
    });

    const fallbackTimer = isEUVisitor() ? null : window.setTimeout(handleInteraction, NON_EU_FALLBACK_MS);

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleInteraction);
      });
      if (fallbackTimer !== null) {
        window.clearTimeout(fallbackTimer);
      }
    };
  }, [userInteracted]);

  useEffect(() => {
    if (!userInteracted) {
      return;
    }

    if (shouldLoadScript()) {
      injectSegmentSnippet();
    }
  }, [userInteracted, injectSegmentSnippet]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const onConsentUpdated = () => {
      if (shouldLoadScript() && !snippetInjected && userInteracted) {
        injectSegmentSnippet();
      }

      if (isSegmentReady() && shouldLoadScript()) {
        firePageIfAllowed();
      }
    };

    window.addEventListener(CONSENT_UPDATED_EVENT, onConsentUpdated);

    return () => {
      window.removeEventListener(CONSENT_UPDATED_EVENT, onConsentUpdated);
    };
  }, [firePageIfAllowed, snippetInjected, userInteracted, injectSegmentSnippet]);

  useEffect(() => {
    if (typeof window === 'undefined' || !snippetInjected) {
      return;
    }

    if (shouldLoadScript() && isSegmentReady()) {
      firePageIfAllowed();
    }
  }, [firePageIfAllowed, snippetInjected]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    (window as any).exportSegmentFormDebugLogs = () => {
      try {
        const data = window.localStorage.getItem(SEGMENT_DEBUG_KEY) || '[]';
        const blob = new Blob([data], { type: 'application/json' });
        const url = URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.download = `segment-form-debug-${new Date().toISOString().slice(0, 10)}.json`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Failed to export Segment debug logs', error);
      }
    };

    (window as any).clearSegmentFormDebugLogs = () => {
      try {
        window.localStorage.removeItem(SEGMENT_DEBUG_KEY);
      } catch (error) {
        console.error('Failed to clear Segment debug logs', error);
      }
    };
  }, []);

  return null;
};

export default LazySegmentScript;
